import { apiClient as api } from './api';
import { callV3, type ApiResponse } from '../api/envelope';

const BASE = '/api/v3/billing';

/** The caller's credit, in Naira. */
export interface CreditBalance {
  balanceNgn: number;
  currency: string;
  /**
   * False while billing runs in shadow mode: costs are computed and logged but
   * nothing is charged or refused. The UI must say "estimated" rather than
   * showing a spend figure it would be wrong to present as money taken.
   */
  enforced: boolean;
  usdToNgn: number;
}

export interface CreditPack {
  id: string;
  name: string;
  ngn: number;
}

export interface CreditPacks {
  currency: string;
  /** False when the payment provider isn't configured — hide buying entirely. */
  purchaseAvailable: boolean;
  packs: CreditPack[];
}

export interface LedgerEntry {
  id: string;
  /** purchase | grant | hold | settle | release | refund | adjustment */
  kind: string;
  amountNgn: number;
  balanceAfterNgn: number;
  lab: string | null;
  provider: string | null;
  model: string | null;
  quantity: number | null;
  operationId: string | null;
  createdAt: string;
}

export interface PurchaseStarted {
  /** Hosted checkout. Open it; never try to embed or replicate it. */
  checkoutUrl: string;
  /** The payment provider's reference, not ours. */
  reference: string;
  ngn: number;
  currency: string;
  packId: string;
}

export interface SyncResult {
  /** Payments rescued by this call. Normally 0 — the webhook usually gets there first. */
  credited: number;
  creditedNgn: number;
  balanceNgn: number;
  currency: string;
}

/** `POST /api/v3/billing/estimate` — what work would cost before running it. */
export interface CreditEstimateRequest {
  /** image | video | text */
  lab: 'image' | 'video' | 'text';
  model: string;
  /** Images for image, seconds for video, output tokens for text. */
  quantity: number;
}

export interface CreditEstimate {
  /** What the wallet would take: ₦0 when the caller's own provider key runs it. */
  costNgn: number;
  /** The provider's price in US dollars, before the rate and markup. */
  costUsd: number;
  ownKey: boolean;
  usdToNgn: number;
  priced: boolean;
  markupPercent: number;
  /** False in shadow mode: an estimate; nothing is charged yet. */
  enforced: boolean;
}

/**
 * Credits and top-ups. Mirrors `BillingController` in the v3 API.
 *
 * Nothing here adds credit — a purchase only starts a hosted checkout. Balance
 * changes arrive via a signed webhook to the API, so the UI learns about them by
 * refetching the balance, never by trusting its own redirect.
 */
export const BillingService = {
  getBalance: () => callV3<CreditBalance>(api.get<ApiResponse<CreditBalance>>(`${BASE}/balance`)),

  getPacks: () => callV3<CreditPacks>(api.get<ApiResponse<CreditPacks>>(`${BASE}/packs`)),

  getLedger: (page = 1, pageSize = 50) =>
    callV3<LedgerEntry[]>(
      api.get<ApiResponse<LedgerEntry[]>>(`${BASE}/ledger`, { params: { page, pageSize } })
    ),

  /** Starts a top-up. Takes a pack id — never an amount, which the server won't accept. */
  startPurchase: (packId: string) =>
    callV3<PurchaseStarted>(
      api.post<ApiResponse<PurchaseStarted>>(`${BASE}/purchase`, { packId })
    ),

  /**
   * Asks the server to reconcile against the payment provider and credit anything the
   * webhook didn't deliver. Idempotent, so it is safe to call on every return from
   * checkout — and cheap enough to be the first thing the billing page does.
   */
  sync: () => callV3<SyncResult>(api.post<ApiResponse<SyncResult>>(`${BASE}/sync`, {})),

  /** The wallet's price for some work, priced exactly as the hold for it will be. */
  estimate: (req: CreditEstimateRequest) =>
    callV3<CreditEstimate>(api.post<ApiResponse<CreditEstimate>>(`${BASE}/estimate`, req)),
};
